import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { FormControlsOf } from '@examdojo/angular/forms';
import {
  ContentItem,
  ContentItemTypeEnum,
  ImageContentItem,
  ImageContentItemFormGroup,
  TableContentItem,
} from '@examdojo/models/content-item';
import { ContentItemEditingOptions } from '@examdojo/models/question-editor';

export function createContentItemsFormArray(
  contentItems: ContentItem[],
  options: ContentItemEditingOptions,
): ContentItemFormArray {
  return new FormArray<ContentItemFormGroup>(contentItems.map((item) => createContentItemFormControl(item, options)));
  // TODO: validation array min length ?
}

export function createQuestionTableFormGroup(
  tableItem: TableContentItem,
  editingOptions: ContentItemEditingOptions,
): TableContentItemFormGroup {
  const controls: TableContentItemFormGroup['controls'] = {
    type: new FormControl(ContentItemTypeEnum.Table, { nonNullable: true }),
    content: new FormControl(tableItem.content, { nonNullable: true }),
    title: new FormControl(tableItem.title, { nonNullable: true }),
    style: new FormControl(tableItem.style, { nonNullable: true }),
    container_width: new FormControl(tableItem.container_width, { nonNullable: true }),
  };

  if (editingOptions.allowFillInTheBlanks) {
    controls.fill_in_the_blanks = new FormControl(tableItem.fill_in_the_blanks, {
      nonNullable: true,
    });
  }

  const fg = new FormGroup(controls) as unknown as TableContentItemFormGroup;

  if (editingOptions.allowImageEditing.length === 0 && !editingOptions.allowTextEditing) {
    fg.disable();
  }

  return fg as unknown as TableContentItemFormGroup;
}

export function createQuestionImageFormGroup(
  imageItem: ImageContentItem,
  editingOptions: ContentItemEditingOptions,
): ImageContentItemFormGroup {
  const controls: ImageContentItemFormGroup['controls'] = {
    type: new FormControl(ContentItemTypeEnum.Image, { nonNullable: true }),
    content: new FormGroup({
      url: new FormControl(imageItem.content.url, { nonNullable: true }),
      original_url: new FormControl(imageItem.content.original_url),
      code: new FormControl(imageItem.content.code),
      generation_completed: new FormControl(imageItem.content.generation_completed, {
        nonNullable: true,
      }),
    }),
    description: new FormControl(imageItem.description, { nonNullable: true }),
    title: new FormControl(imageItem.title, { nonNullable: true }),
    not_to_scale: new FormControl(imageItem.not_to_scale, { nonNullable: true }),
    style: new FormControl(imageItem.style, { nonNullable: true }),
    container_width: new FormControl(imageItem.container_width, { nonNullable: true }),
  };

  if (editingOptions.allowFillInTheBlanks) {
    controls.fill_in_the_blanks = new FormControl(imageItem.fill_in_the_blanks, {
      nonNullable: true,
    });
  }

  const fg: ImageContentItemFormGroup = new FormGroup(controls) as unknown as ImageContentItemFormGroup;

  if (editingOptions.allowImageEditing.length === 0) {
    fg.controls.content.disable();
    fg.controls.description?.disable();
    fg.controls.title?.disable();
    fg.controls.not_to_scale?.disable();
    fg.controls.style?.disable();
    fg.controls.container_width?.disable();
  }

  return fg;
}

export function createContentItemFormControl(
  contentItem: ContentItem,
  editingOptions: ContentItemEditingOptions,
): ContentItemFormGroup {
  switch (contentItem.type) {
    case ContentItemTypeEnum.Text: {
      const fg = new FormGroup({
        type: new FormControl(contentItem.type, { nonNullable: true }),
        content: new FormControl(contentItem.content, { nonNullable: true }),
      });

      if (!editingOptions.allowTextEditing) {
        fg.disable();
      }

      return fg;
    }

    case ContentItemTypeEnum.Image: {
      return createQuestionImageFormGroup(contentItem, editingOptions);
    }

    case ContentItemTypeEnum.Table:
      return createQuestionTableFormGroup(contentItem, editingOptions);
  }
}

export type ContentItemFormArray = FormArray<ContentItemFormGroup>;

export type TableContentItemFormGroup = FormGroup<{
  type: FormControl<ContentItemTypeEnum.Table>;
  content: FormControl<string>;
  title: FormControl<string | undefined>;
  style: FormControl<Partial<CSSStyleDeclaration> | undefined>;
  container_width: FormControl<string | undefined>;
  fill_in_the_blanks?: FormControl<boolean | undefined>;
}>;

export type TextContentItemFormGroup = FormGroup<FormControlsOf<{ type: ContentItemTypeEnum.Text; content: string }>>;

export type ContentItemFormGroup = TextContentItemFormGroup | ImageContentItemFormGroup | TableContentItemFormGroup;
